import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { invokeEdgeFunction } from '@/utils/edgeFunctionUtils';
import { supabase } from '@/integrations/supabase/client';

interface CorrelationState {
  originPage: string;
  assetId: string;
  collectionId: string;
  reopenCropEditor?: boolean;
  customReturnPath?: string;
  newsItemId?: string;
  openModal?: boolean;
}

type ProcessingStatus = 'processing' | 'exporting' | 'downloading' | 'success' | 'error';

const PROGRESS_STEPS = [
  { key: 'processing', label: 'Processing design...', progress: 25 },
  { key: 'exporting', label: 'Exporting high-quality image...', progress: 60 },
  { key: 'downloading', label: 'Updating your asset...', progress: 90 },
  { key: 'success', label: 'Complete!', progress: 100 }
];

const CanvaReturn: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [status, setStatus] = useState<ProcessingStatus>('processing');
  const [error, setError] = useState<string | null>(null);
  const [correlationState, setCorrelationState] = useState<CorrelationState | null>(null);
  const [redirectUrl, setRedirectUrl] = useState<string>('');

  const designId = searchParams.get('design_id');
  const correlationStateStr = searchParams.get('correlation_state');

  useEffect(() => {
    if (designId && correlationStateStr) {
      processReturn();
    } else {
      setStatus('error');
      setError('Missing required parameters from Canva return');
    }
  }, [designId, correlationStateStr]);

  const decodeCorrelationState = (encodedState: string): CorrelationState | null => {
    try {
      const decodedJson = atob(decodeURIComponent(encodedState));
      return JSON.parse(decodedJson);
    } catch (error) {
      console.error('Failed to decode correlation state:', error);
      return null;
    }
  };

  const processReturn = async () => {
    if (!designId || !correlationStateStr) return;

    try {
      console.log('🔄 [Canva Return] Processing return from Canva');
      console.log('📋 [Canva Return] Design ID:', designId);
      console.log('🔗 [Canva Return] Correlation state:', correlationStateStr);

      // Decode correlation state - if empty, try localStorage
      let decodedState: CorrelationState | null = null;
      
      if (correlationStateStr && correlationStateStr.trim()) {
        decodedState = decodeCorrelationState(correlationStateStr);
        console.log('📤 [Canva Return] Decoded correlation state:', decodedState);
      }
      
      // If still no correlation state, try localStorage as backup
      if (!decodedState && designId) {
        const correlationKey = `canva_correlation_${designId}`;
        const storedState = localStorage.getItem(correlationKey);
        if (storedState) {
          console.log('🔄 [Canva Return] Using stored correlation state from localStorage');
          try {
            decodedState = JSON.parse(storedState);
            // Clean up localStorage
            localStorage.removeItem(correlationKey);
          } catch (error) {
            console.error('Failed to parse stored correlation state:', error);
          }
        }
      }
      
      if (!decodedState) {
        throw new Error('Failed to decode correlation state - no state available');
      }
      
      setCorrelationState(decodedState);
      console.log('✅ [Canva Return] Decoded correlation state:', decodedState);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // Step 1: Process design
      setStatus('processing');

      // Step 2: Export the design
      setStatus('exporting');

      const { data: exportResult, error: exportError } = await invokeEdgeFunction(
        'canva-export-design',
        {
          designId: designId,
          format: 'png',
          quality: 'high'
        },
        {
          'Authorization': `Bearer ${session.access_token}`
        }
      );

      if (exportError || !exportResult?.success) {
        throw new Error(exportError?.message || 'Failed to export design');
      }

      console.log('✅ [Canva Return] Design exported successfully');

      // Step 3: Update the asset with the exported image URL
      setStatus('downloading');

      // The canva-export-design function already saved the file to storage
      // Now we need to update the asset record with the new URL
      const editedImageUrl = exportResult.storage_url || exportResult.download_url;
      
      if (editedImageUrl) {
        console.log('🔗 [Canva Return] Updating asset URL to:', editedImageUrl);
        
        // Determine which fields to update based on which image was edited
        // Check localStorage for the design key to determine image type
        const primaryDesignKey = `canva_design_${decodedState.assetId}_primary`;
        const secondaryDesignKey = `canva_design_${decodedState.assetId}_secondary`;
        const legacyDesignKey = `canva_design_${decodedState.assetId}`;
        
        let isEditingSecondary = false;
        
        // Check if current designId matches stored secondary design
        const secondaryDesignData = localStorage.getItem(secondaryDesignKey);
        if (secondaryDesignData) {
          try {
            const parsedSecondary = JSON.parse(secondaryDesignData);
            if (parsedSecondary.designId === designId) {
              isEditingSecondary = true;
            }
          } catch (error) {
            console.warn('Failed to parse secondary design data:', error);
          }
        }
        
        let updateFields: any = {
          updated_at: new Date().toISOString()
        };
        
        if (isEditingSecondary) {
          updateFields.secondary_markup_url = editedImageUrl;
          updateFields.secondary_image = editedImageUrl;
        } else {
          updateFields.primary_markup_url = editedImageUrl;
          updateFields.primary_image = editedImageUrl;
        }
        
        const { error: updateError } = await supabase
          .from('app_assets')
          .update(updateFields)
          .eq('id', decodedState.assetId);

        if (updateError) {
          console.error('❌ [Canva Return] Failed to update asset:', updateError);
          throw new Error('Failed to update asset with edited image');
        }

        console.log('✅ [Canva Return] Asset updated successfully');
      } else {
        console.warn('⚠️ [Canva Return] No image URL available from export result');
      }

      // Step 4: Success and prepare redirect
      setStatus('success');
      
      // Prepare redirect URL based on origin page
      let finalUrl = '';
      
      if (decodedState.originPage === 'NEWS_MODAL' && decodedState.customReturnPath) {
        // For news modal, use custom return path
        finalUrl = decodedState.customReturnPath;
      } else if (decodedState.collectionId && decodedState.assetId) {
        // Default behavior for ASSET_EDITOR
        const params = new URLSearchParams();
        params.set('openAssetEditor', decodedState.assetId);
        params.set('reopenCropEditor', 'true');
        params.set('editorMode', 'markup');
        
        finalUrl = `/collections/${decodedState.collectionId}?${params.toString()}`;
      }
      
      if (finalUrl) {
        setRedirectUrl(finalUrl);
        
        // Auto-redirect after 2 seconds
        setTimeout(() => {
          window.location.href = finalUrl;
        }, 2000);
      }

      toast({
        title: "Complete!",
        description: "Returning to editor...",
      });

    } catch (error) {
      console.error('❌ [Canva Return] Error processing return:', error);
      setStatus('error');
      setError(error instanceof Error ? error.message : 'Unknown error occurred');
      
      toast({
        title: "Processing Failed",
        description: error instanceof Error ? error.message : "Failed to process your Canva edits.",
        variant: "destructive",
      });
    }
  };

  const redirectToOrigin = () => {
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  };

  const getCurrentStep = () => {
    return PROGRESS_STEPS.find(step => step.key === status) || PROGRESS_STEPS[0];
  };

  const getProgressValue = () => {
    const currentStep = getCurrentStep();
    return currentStep.progress;
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="w-full max-w-md px-8">
        {/* Status Icon */}
        <div className="text-center mb-8">
          {status === 'error' ? (
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          ) : status === 'success' ? (
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          ) : (
            <Loader2 className="h-16 w-16 text-blue-500 mx-auto mb-4 animate-spin" />
          )}
        </div>

        {/* Progress Bar */}
        {status !== 'error' && (
          <div className="mb-8">
            <Progress value={getProgressValue()} className="h-2 mb-4" />
            <div className="text-center">
              <p className="text-lg font-medium text-gray-900 mb-2">
                {getCurrentStep().label}
              </p>
              {status === 'success' && (
                <p className="text-sm text-gray-600">
                  Redirecting automatically...
                </p>
              )}
            </div>
          </div>
        )}

        {/* Error State */}
        {status === 'error' && (
          <div className="text-center space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">Processing Failed</h2>
            <p className="text-gray-600 mb-6">{error || 'An error occurred while processing your return from Canva.'}</p>
            
            <div className="space-y-2">
              <Button
                onClick={() => window.location.reload()}
                className="w-full"
              >
                Try Again
              </Button>
              <Button
                variant="outline"
                onClick={redirectToOrigin}
                className="w-full"
              >
                Back to Collections
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CanvaReturn;